import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import Seo from '../../components/Seo';
import { SITE_URL } from '../../lib/structuredData';
import { CAMPUS_GALLETAS, SARA, COPY, PAGO, BACKEND_URL, cutoutImage } from './config';
import {
  carritoItems, totalGalletas, totalUSD, money,
  fmtDiaFijo, nombreDiaFijo, fmtCierre, buildWaText, waLink,
} from './helpers';
import './udla-theme.css';

// Chip informativo del header (pill blanca sobre el header rosa).
const Chip = ({ children }) => (
  <span className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1.5 text-xs font-bold text-[var(--udla-ink)] shadow-sm">
    {children}
  </span>
);

// Stepper +/- para un sabor. Touch targets ≥44px, accesible por teclado.
// El número hace un pop sutil (transform, no layout) cada vez que cambia.
const Stepper = ({ qty, onDec, onInc, name }) => (
  <div className="flex items-center gap-2.5">
    <button
      type="button"
      onClick={onDec}
      disabled={qty === 0}
      aria-label={`Quitar una ${name}`}
      className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-[var(--udla-ink)]/15 text-[var(--udla-ink)] text-xl font-bold transition active:scale-90 disabled:opacity-30 disabled:active:scale-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--udla-pink-deep)]"
    >
      −
    </button>
    <span
      key={qty}
      className="udla-qty-pop w-6 text-center text-lg font-extrabold text-[var(--udla-ink)] tabular-nums"
      aria-live="polite"
    >
      {qty}
    </span>
    <button
      type="button"
      onClick={onInc}
      aria-label={`Agregar una ${name}`}
      className="flex h-11 w-11 items-center justify-center rounded-full bg-[var(--udla-pink-deep)] text-white text-xl font-bold transition active:scale-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--udla-pink-deep)]"
    >
      +
    </button>
  </div>
);

export default function GalletasSara() {
  // Un pedido es de UN solo tipo: "hoy" (venta del día), "preventa" (día
  // fijo) u "otro día" coordinado. `modo` se fija con el primer +1 y se
  // libera automáticamente cuando los 3 carritos quedan vacíos (ver efecto
  // más abajo) — evita mezclar tipos en un pedido.
  const [modo, setModo] = useState(null); // 'hoy' | 'preventa' | 'coordinado' | null
  const [cantidadesHoy, setCantidadesHoy] = useState({});
  const [cantidadesPreventa, setCantidadesPreventa] = useState({});
  const [cantidadesCoordinado, setCantidadesCoordinado] = useState({});
  const [fechaDeseada, setFechaDeseada] = useState('');
  const [horaDeseada, setHoraDeseada] = useState('');
  const [avisoModo, setAvisoModo] = useState('');

  const [config, setConfig] = useState(null);   // null hasta que responda el API
  const [apiDown, setApiDown] = useState(false);
  const [nombre, setNombre] = useState('');
  const [metodo, setMetodo] = useState('efectivo');
  const [deunaQrOk, setDeunaQrOk] = useState(true); // false si la imagen del QR falla (aún no la subieron)
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [confirmacion, setConfirmacion] = useState(null); // {numero|null, viernes, tipo}
  const [touched, setTouched] = useState(false);
  const formRef = useRef(null);
  const metodoPanelRef = useRef(null);

  // Al elegir transferencia/De Una, el panel con los datos/QR se revela DEBAJO
  // de los botones — sin esto queda fuera de vista y parece que no pasó nada.
  useEffect(() => {
    if (metodo === 'transferencia' || metodo === 'deuna') {
      metodoPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [metodo]);

  // Cargar config (estado abierto/cerrado, stock restante, punto/hora reales,
  // venta del día). NO se ejecuta durante el prerender (Chrome headless:
  // navigator.webdriver): así el snapshot queda SIN datos del API (config=null)
  // y coincide exactamente con el primer render del cliente, evitando un
  // hydration mismatch (React #418). En el navegador real corre normalmente
  // tras hidratar. Mismo patrón que el Toaster en App.js.
  useEffect(() => {
    if (typeof navigator !== 'undefined' && navigator.webdriver) return;
    let alive = true;
    fetch(`${BACKEND_URL}/api/campus/config`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('bad status'))))
      .then((cfg) => alive && setConfig(cfg))
      .catch(() => alive && setApiDown(true));
    return () => { alive = false; };
  }, []);

  const restantes = config && typeof config.restantes === 'number' ? config.restantes : null;
  const cerrado = config ? config.abierto === false : false;
  const punto = config?.punto_entrega || SARA.puntoEntrega;
  const hora = config?.hora_entrega || SARA.horaEntrega;
  const viernesIso = config?.viernes || null;
  // useMemo (no `|| []` inline) para no crear un array nuevo cada render —
  // eso invalidaría el useCallback de onChangeFecha en cada render.
  const diasBloqueados = useMemo(() => config?.dias_bloqueados || [], [config]);

  // Fecha ISO de hoy en hora local del dispositivo (no toISOString/UTC, para
  // no correrse un día cerca de la medianoche) — solo sirve de `min` en el
  // input; la validación real (incluyendo pasado/bloqueados) la hace el server.
  const hoyIso = useMemo(() => {
    const d = new Date();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
  }, []);

  // Venta del día: solo presente si el backend la expone (activa y con stock).
  const ventaDia = config?.venta_dia || null;
  const saboresHoy = useMemo(() => {
    if (!ventaDia?.stock) return [];
    return CAMPUS_GALLETAS.filter((g) => (ventaDia.stock[g.id] || 0) > 0);
  }, [ventaDia]);

  const itemsHoy = useMemo(
    () => carritoItems(cantidadesHoy, saboresHoy),
    [cantidadesHoy, saboresHoy]
  );
  const itemsPreventa = useMemo(
    () => carritoItems(cantidadesPreventa, CAMPUS_GALLETAS),
    [cantidadesPreventa]
  );
  const itemsCoordinado = useMemo(
    () => carritoItems(cantidadesCoordinado, CAMPUS_GALLETAS),
    [cantidadesCoordinado]
  );
  const items = modo === 'hoy' ? itemsHoy : modo === 'coordinado' ? itemsCoordinado : itemsPreventa;
  const total = totalGalletas(items);

  // Si los 3 carritos quedan en 0, se libera el bloqueo de modo (permite
  // alternar de sección de nuevo).
  useEffect(() => {
    if (
      modo
      && totalGalletas(itemsHoy) === 0
      && totalGalletas(itemsPreventa) === 0
      && totalGalletas(itemsCoordinado) === 0
    ) {
      setModo(null);
      setAvisoModo('');
      setFechaDeseada('');
      setHoraDeseada('');
    }
  }, [modo, itemsHoy, itemsPreventa, itemsCoordinado]);

  // Devuelve false (y muestra un aviso) si el usuario intenta agregar de la
  // OTRA sección mientras ya tiene algo en el carrito — un pedido es de un solo tipo.
  const tryStartModo = useCallback((target) => {
    if (modo && modo !== target) {
      const label = modo === 'hoy' ? 'HOY' : modo === 'coordinado' ? 'coordinar otro día' : 'la pre-venta';
      setAvisoModo(
        `Tu pedido ya tiene galletas de ${label}. `
        + 'Vacíalo (quita las galletas) si quieres cambiar de sección.'
      );
      return false;
    }
    setAvisoModo('');
    return true;
  }, [modo]);

  const incHoy = useCallback((id) => {
    if (!tryStartModo('hoy')) return;
    setError('');
    setCantidadesHoy((prev) => {
      const max = ventaDia?.stock?.[id] || 0;
      const next = (prev[id] || 0) + 1;
      if (next > max) {
        setError(`Solo quedan ${max} para hoy 🍪`);
        return prev;
      }
      setModo('hoy');
      return { ...prev, [id]: next };
    });
  }, [tryStartModo, ventaDia]);

  const decHoy = useCallback((id) => {
    setError('');
    setCantidadesHoy((prev) => ({ ...prev, [id]: Math.max(0, (prev[id] || 0) - 1) }));
  }, []);

  const incPreventa = useCallback((id) => {
    if (!tryStartModo('preventa')) return;
    setError('');
    setCantidadesPreventa((prev) => {
      const next = (prev[id] || 0) + 1;
      const nuevoTotal = totalGalletas(itemsPreventa) + 1;
      if (restantes != null && nuevoTotal > restantes) {
        setError(
          restantes <= 0
            ? '¡Se agotaron las galletas de este viernes! 😔'
            : `Solo quedan ${restantes} galletas para este viernes. Ajusta tu pedido 🍪`
        );
        return prev;
      }
      setModo('preventa');
      return { ...prev, [id]: next };
    });
  }, [tryStartModo, restantes, itemsPreventa]);

  const decPreventa = useCallback((id) => {
    setError('');
    setCantidadesPreventa((prev) => ({ ...prev, [id]: Math.max(0, (prev[id] || 0) - 1) }));
  }, []);

  // "Otro día": sin tope de stock (no hay stock automático, Sara coordina
  // manualmente), a diferencia de incHoy/incPreventa.
  const incCoordinado = useCallback((id) => {
    if (!tryStartModo('coordinado')) return;
    setError('');
    setModo('coordinado');
    setCantidadesCoordinado((prev) => ({ ...prev, [id]: (prev[id] || 0) + 1 }));
  }, [tryStartModo]);

  const decCoordinado = useCallback((id) => {
    setError('');
    setCantidadesCoordinado((prev) => ({ ...prev, [id]: Math.max(0, (prev[id] || 0) - 1) }));
  }, []);

  // Chequeo en cliente (defensa en profundidad, no autoritativo — el server
  // vuelve a validar) contra las fechas que Sara bloqueó desde su panel.
  const onChangeFecha = useCallback((v) => {
    setFechaDeseada(v);
    setError(diasBloqueados.includes(v) ? 'Esa fecha ya no está disponible, elige otra 📅' : '');
  }, [diasBloqueados]);

  const formValido = nombre.trim().length >= 2
    && (modo !== 'coordinado' || (fechaDeseada && horaDeseada.trim().length > 0));

  const onSubmit = async () => {
    setTouched(true);
    setError('');
    if (total === 0) return;
    if (!formValido) {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    const tipo = modo === 'hoy' ? 'dia' : modo === 'coordinado' ? 'coordinado' : 'preventa';
    if (tipo === 'coordinado' && diasBloqueados.includes(fechaDeseada)) {
      setError('Esa fecha ya no está disponible, elige otra 📅');
      return;
    }
    setSubmitting(true);
    const payload = {
      nombre: nombre.trim(),
      metodo_pago: metodo,
      tipo,
      items: items.map((it) => ({ galleta_id: it.id, name: it.name, qty: it.qty })),
      ...(tipo === 'coordinado' ? { fecha_deseada: fechaDeseada, hora_deseada: horaDeseada.trim() } : {}),
    };
    try {
      const res = await fetch(`${BACKEND_URL}/api/campus/pedidos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.status === 409) {
        const body = await res.json().catch(() => ({}));
        setError(body.detail || 'No pudimos registrar el pedido. Ajusta las cantidades.');
        setSubmitting(false);
        return;
      }
      if (!res.ok) throw new Error('bad status');
      const data = await res.json();
      setConfirmacion({ numero: data.numero, viernes: data.viernes, tipo, fechaDeseada, horaDeseada });
    } catch {
      // Degradación elegante: seguimos por WhatsApp sin número de pedido.
      setConfirmacion({ numero: null, viernes: viernesIso, tipo, fechaDeseada, horaDeseada });
    } finally {
      setSubmitting(false);
    }
  };

  const waText = confirmacion
    ? buildWaText({
        tipo: confirmacion.tipo,
        numero: confirmacion.numero,
        items,
        nombre: nombre.trim(),
        metodo,
        viernesIso: confirmacion.viernes || viernesIso,
        punto,
        hora,
        fechaDeseada: confirmacion.fechaDeseada,
        horaDeseada: confirmacion.horaDeseada,
      })
    : '';

  const resetPedido = () => {
    setConfirmacion(null);
    setCantidadesHoy({});
    setCantidadesPreventa({});
    setCantidadesCoordinado({});
    setFechaDeseada('');
    setHoraDeseada('');
    setModo(null);
    setNombre('');
    setMetodo('efectivo');
    setTouched(false);
    setError('');
    setAvisoModo('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const seo = (
    <Seo
      title="Galletas de Sara 🍪 — pide para este viernes"
      description="Galletas artesanales recién horneadas a $1. Pide antes del jueves y retíralas el viernes en la UDLA. Powered by Brazolovers."
      path="/udla"
      image={`${SITE_URL}/og-udla.jpg`}
      imageWidth={1200}
      imageHeight={1200}
      imageAlt="Galletas de Sara — galletas artesanales a $1, powered by Brazolovers"
    />
  );

  // ── Pantalla de confirmación ───────────────────────────────────────────────
  if (confirmacion) {
    const esHoy = confirmacion.tipo === 'dia';
    const esCoordinado = confirmacion.tipo === 'coordinado';
    return (
      <div className="udla-theme min-h-screen bg-[var(--udla-bg)]">
        {seo}
        <div className="mx-auto flex min-h-screen max-w-[480px] flex-col items-center justify-center px-5 py-10 text-center">
          <div className="text-6xl" aria-hidden="true">🍪</div>
          {esHoy && (
            <span className="mt-3 inline-block rounded-full bg-[var(--udla-pink-deep)] px-3 py-1 text-xs font-extrabold text-white">
              RETIRO HOY
            </span>
          )}
          {esCoordinado && (
            <span className="mt-3 inline-block rounded-full bg-amber-500 px-3 py-1 text-xs font-extrabold text-white">
              SOLICITUD PENDIENTE
            </span>
          )}
          <h1 className="mt-3 text-3xl text-[var(--udla-ink)]">
            {confirmacion.numero ? `¡Pedido #${confirmacion.numero} registrado!` : '¡Pedido listo!'}
          </h1>
          <p className="mt-2 font-semibold text-[var(--udla-ink)]">
            {esHoy && `${total} galleta${total === 1 ? '' : 's'} — ${money(totalUSD(items))} · retiro hoy`}
            {esCoordinado && `${total} galleta${total === 1 ? '' : 's'} para ${fmtDiaFijo(confirmacion.fechaDeseada)} · ${confirmacion.horaDeseada} — ${money(totalUSD(items))}`}
            {!esHoy && !esCoordinado && `${total} galleta${total === 1 ? '' : 's'} para ${fmtDiaFijo(confirmacion.viernes || viernesIso)} — ${money(totalUSD(items))}`}
          </p>
          {esCoordinado ? (
            <p className="mt-1 text-sm text-[var(--udla-ink-muted)]">
              Sara debe confirmarte por WhatsApp que puede cumplir esa fecha y hora.
            </p>
          ) : (
            <p className="mt-1 text-sm text-[var(--udla-ink-muted)]">
              Retiro: {hora} · {punto}
            </p>
          )}
          <p className="mt-4 text-sm text-[var(--udla-ink-muted)]">
            Falta un paso: mándale el pedido a Sara para confirmarlo 👇
          </p>
          <a
            href={waLink(waText)}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-green-600 px-6 py-4 text-lg font-bold text-white shadow-lg transition active:scale-95"
          >
            {COPY.ctaConfirmar}
          </a>
          <button
            type="button"
            onClick={resetPedido}
            className="mt-4 text-sm font-bold text-[var(--udla-ink)] underline underline-offset-2"
          >
            Hacer otro pedido
          </button>
        </div>
      </div>
    );
  }

  // ── Página de pedido ───────────────────────────────────────────────────────
  return (
    <div className="udla-theme min-h-screen bg-[var(--udla-bg)] pb-32">
      {seo}

      {/* Header — banda rosa + fotos reales SIN fondo (recorte por IA, no la
          foto de estudio con fondo negro) flotando en fila, sin marco. En
          fila normal (no absolute) para que NUNCA pisen el título sin
          importar el ancho de pantalla — un collage con posición absoluta se
          ve bien en desktop ancho pero se monta sobre el texto en un teléfono
          real de ~380px (verificado). El texto siempre va sobre el rosa
          sólido (contraste ya verificado), nunca encima de una foto. */}
      <header className="relative overflow-hidden rounded-b-[2.25rem] bg-[var(--udla-pink)] px-5 pb-7 pt-8 text-center">
        <div className="mb-1 flex items-end justify-center gap-1">
          <img
            src={cutoutImage('coco')}
            alt=""
            aria-hidden="true"
            className="udla-sticker relative z-10 h-14 w-14 object-contain drop-shadow-lg"
            style={{ '--udla-rot': '-10deg', animationDelay: '1.1s' }}
          />
          <img
            src={cutoutImage('choco_chips')}
            alt=""
            aria-hidden="true"
            className="udla-sticker relative z-20 h-16 w-16 object-contain drop-shadow-lg"
            style={{ '--udla-rot': '6deg', animationDelay: '0.2s' }}
          />
          <img
            src={cutoutImage('red_velvet')}
            alt=""
            aria-hidden="true"
            className="udla-sticker relative z-10 h-14 w-14 object-contain drop-shadow-lg"
            style={{ '--udla-rot': '12deg', animationDelay: '0.6s' }}
          />
        </div>
        <div className="relative mx-auto max-w-[480px]">
          <h1 className="text-[clamp(2.25rem,9vw,3rem)] leading-[0.95] text-[var(--udla-ink)]">
            {COPY.marca} 🍪
          </h1>
          <p className="mt-3 text-base font-bold text-[var(--udla-ink)]">{COPY.tagline}</p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
            <span className="inline-flex items-center rounded-full bg-white/70 px-2.5 py-1 text-[11px] font-bold text-[var(--udla-ink)]">
              {COPY.endoso}
            </span>
          </div>
          <div className="mt-3 flex flex-wrap justify-center gap-2">
            <Chip>📅 {nombreDiaFijo(viernesIso) || SARA.diaEntrega} {hora}</Chip>
            <Chip>📍 {punto}</Chip>
            <Chip>⏰ Cierra {fmtCierre(config?.cierre) || SARA.cierre}</Chip>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[480px] px-5">
        {avisoModo && (
          <p className="mt-4 rounded-xl bg-amber-50 px-4 py-2 text-center text-xs font-medium text-amber-900" role="alert">
            {avisoModo}
          </p>
        )}

        {/* ── Venta del día: sección "HOY" (independiente del cierre de preventa) ── */}
        {saboresHoy.length > 0 && (
          <>
            <section className="mt-5">
              <h2 className="text-xl text-[var(--udla-mint-deep)]">{COPY.hoyTitulo}</h2>
              <p className="mb-3 text-xs font-semibold text-[var(--udla-ink-muted)]">{COPY.hoySubtitulo}</p>
              <ul className="space-y-3">
                {saboresHoy.map((g, i) => {
                  const max = ventaDia.stock[g.id] || 0;
                  const qty = cantidadesHoy[g.id] || 0;
                  return (
                    <li
                      key={g.id}
                      style={{ animationDelay: `${i * 40}ms` }}
                      className={`udla-rise overflow-hidden rounded-2xl shadow-sm transition ${
                        qty > 0 ? 'ring-2 ring-[var(--udla-mint-deep)]' : 'ring-1 ring-black/5'
                      } bg-[var(--udla-surface)]`}
                    >
                      <div className="relative flex h-36 w-full items-center justify-center bg-[var(--udla-surface)] p-3">
                        <img
                          src={cutoutImage(g.id)}
                          alt={g.name}
                          loading="lazy"
                          style={{ animationDelay: `${i * 220}ms` }}
                          className="udla-card-float h-full w-full object-contain drop-shadow-md"
                        />
                        <span className="absolute right-3 top-3 rounded-full bg-[var(--udla-mint-deep)] px-2.5 py-1 text-xs font-extrabold text-white shadow">
                          Quedan {max - qty}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 p-3">
                        <p className="min-w-0 flex-1 text-base font-extrabold text-[var(--udla-ink)]">{g.name}</p>
                        <Stepper qty={qty} name={g.name} onInc={() => incHoy(g.id)} onDec={() => decHoy(g.id)} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>

            <div className="my-6 flex items-center gap-3">
              <div className="h-px flex-1 bg-[var(--udla-ink)]/10" />
              <span className="text-xs font-bold text-[var(--udla-ink-muted)]">o</span>
              <div className="h-px flex-1 bg-[var(--udla-ink)]/10" />
            </div>
          </>
        )}

        {/* Banner de stock de preventa (elemento firma) */}
        {restantes != null && !cerrado && (
          <div
            className="mt-4 rounded-2xl border border-amber-300 bg-amber-100 px-4 py-3 text-center text-sm font-bold text-amber-900"
            aria-live="polite"
          >
            🔥 {restantes > 0
              ? `Quedan ${restantes} galletas para este viernes`
              : 'Se agotaron las de este viernes 😔'}
          </div>
        )}

        {/* Preventa: cerrada o lista de sabores */}
        {cerrado ? (
          <div className="mt-8 rounded-2xl bg-[var(--udla-surface)] p-6 text-center shadow-sm ring-1 ring-black/5">
            <div className="text-4xl" aria-hidden="true">😴</div>
            <p className="mt-3 font-semibold text-[var(--udla-ink-muted)]">{COPY.cerrado}</p>
          </div>
        ) : (
          <>
            <h2 className="mb-1 mt-6 text-xl text-[var(--udla-pink-deep)]">
              {saboresHoy.length > 0 ? `📅 O pide para ${fmtDiaFijo(viernesIso)}` : COPY.elegir}
            </h2>

            <ul className="mt-3 space-y-3">
              {CAMPUS_GALLETAS.map((g, i) => {
                const qty = cantidadesPreventa[g.id] || 0;
                return (
                  <li
                    key={g.id}
                    style={{ animationDelay: `${i * 40}ms` }}
                    className={`udla-rise overflow-hidden rounded-2xl shadow-sm transition ${
                      qty > 0 ? 'ring-2 ring-[var(--udla-pink-deep)]' : 'ring-1 ring-black/5'
                    } bg-[var(--udla-surface)]`}
                  >
                    <div className="flex h-36 w-full items-center justify-center bg-[var(--udla-pink-soft)] p-3">
                      <img
                        src={cutoutImage(g.id)}
                        alt={g.name}
                        loading="lazy"
                        style={{ animationDelay: `${i * 220}ms` }}
                        className="udla-card-float h-full w-full object-contain drop-shadow-md"
                      />
                    </div>
                    <div className="flex items-center gap-3 p-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-base font-extrabold text-[var(--udla-ink)]">{g.name}</p>
                        <p className="line-clamp-1 text-xs font-medium text-[var(--udla-ink-muted)]">{g.description}</p>
                      </div>
                      <Stepper qty={qty} name={g.name} onInc={() => incPreventa(g.id)} onDec={() => decPreventa(g.id)} />
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}

        {/* ── "Otro día": coordinación manual con Sara, independiente del ── */}
        {/* cierre del día fijo (siempre visible, como la sección "HOY"). ── */}
        <div className="my-6 flex items-center gap-3">
          <div className="h-px flex-1 bg-[var(--udla-ink)]/10" />
          <span className="text-xs font-bold text-[var(--udla-ink-muted)]">o</span>
          <div className="h-px flex-1 bg-[var(--udla-ink)]/10" />
        </div>

        <section className="mt-5">
          <h2 className="text-xl text-[var(--udla-pink-deep)]">📅 ¿Otro día? Coordina aquí</h2>
          <p className="mb-3 text-xs font-semibold text-[var(--udla-ink-muted)]">
            Elige fecha y hora; Sara te confirma por WhatsApp si puede cumplir.
          </p>
          <ul className="mt-3 space-y-3">
            {CAMPUS_GALLETAS.map((g, i) => {
              const qty = cantidadesCoordinado[g.id] || 0;
              return (
                <li
                  key={g.id}
                  style={{ animationDelay: `${i * 40}ms` }}
                  className={`udla-rise overflow-hidden rounded-2xl shadow-sm transition ${
                    qty > 0 ? 'ring-2 ring-[var(--udla-pink-deep)]' : 'ring-1 ring-black/5'
                  } bg-[var(--udla-surface)]`}
                >
                  <div className="flex h-36 w-full items-center justify-center bg-[var(--udla-pink-soft)] p-3">
                    <img
                      src={cutoutImage(g.id)}
                      alt={g.name}
                      loading="lazy"
                      style={{ animationDelay: `${i * 220}ms` }}
                      className="udla-card-float h-full w-full object-contain drop-shadow-md"
                    />
                  </div>
                  <div className="flex items-center gap-3 p-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-base font-extrabold text-[var(--udla-ink)]">{g.name}</p>
                      <p className="line-clamp-1 text-xs font-medium text-[var(--udla-ink-muted)]">{g.description}</p>
                    </div>
                    <Stepper qty={qty} name={g.name} onInc={() => incCoordinado(g.id)} onDec={() => decCoordinado(g.id)} />
                  </div>
                </li>
              );
            })}
          </ul>

          {totalGalletas(itemsCoordinado) > 0 && (
            <div className="mt-4 space-y-3 rounded-2xl bg-[var(--udla-surface)] p-4 shadow-sm ring-1 ring-black/5">
              <label className="block">
                <span className="text-sm font-bold text-[var(--udla-ink)]">Fecha</span>
                <input
                  type="date"
                  min={hoyIso}
                  value={fechaDeseada}
                  onChange={(e) => onChangeFecha(e.target.value)}
                  className="mt-1 w-full rounded-xl border-2 border-[var(--udla-ink)]/10 px-3 py-3 text-[var(--udla-ink)] outline-none focus:border-[var(--udla-pink-deep)]"
                />
              </label>
              <label className="block">
                <span className="text-sm font-bold text-[var(--udla-ink)]">Hora</span>
                <input
                  type="text"
                  maxLength={40}
                  value={horaDeseada}
                  onChange={(e) => setHoraDeseada(e.target.value)}
                  placeholder="Ej. 3:00 pm"
                  className="mt-1 w-full rounded-xl border-2 border-[var(--udla-ink)]/10 px-3 py-3 text-[var(--udla-ink)] outline-none focus:border-[var(--udla-pink-deep)]"
                />
              </label>
            </div>
          )}
        </section>

        {error && (
          <p className="mt-3 rounded-xl bg-red-50 px-4 py-2 text-center text-sm font-bold text-red-700" role="alert">
            {error}
          </p>
        )}

        {/* Formulario (aparece con ≥1 galleta, de cualquiera de las 2 secciones) */}
        {total > 0 && (
          <section ref={formRef} className="mt-6 rounded-2xl bg-[var(--udla-surface)] p-4 shadow-sm ring-1 ring-black/5">
            <h2 className="text-lg text-[var(--udla-ink)]">Tus datos</h2>
            <div className="mt-3 space-y-3">
              <label className="block">
                <span className="text-sm font-bold text-[var(--udla-ink)]">Nombre</span>
                <input
                  type="text"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  placeholder="¿Cómo te llamas?"
                  autoComplete="name"
                  className="mt-1 w-full rounded-xl border-2 border-[var(--udla-ink)]/10 px-3 py-3 text-[var(--udla-ink)] outline-none focus:border-[var(--udla-pink-deep)] focus-visible:ring-2 focus-visible:ring-[var(--udla-ring)]"
                />
                {touched && nombre.trim().length < 2 && (
                  <span className="mt-1 block text-xs font-semibold text-red-600">Escribe tu nombre.</span>
                )}
              </label>
              <fieldset>
                <legend className="text-sm font-bold text-[var(--udla-ink)]">Método de pago</legend>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {[
                    { v: 'efectivo', label: 'Efectivo' },
                    { v: 'transferencia', label: 'Transferencia' },
                    { v: 'deuna', label: 'De Una' },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setMetodo(opt.v)}
                      aria-pressed={metodo === opt.v}
                      className={`rounded-xl border-2 px-2 py-3 text-xs font-bold transition active:scale-95 sm:text-sm ${
                        metodo === opt.v
                          ? 'border-[var(--udla-pink-deep)] bg-[var(--udla-pink-soft)] text-[var(--udla-ink)]'
                          : 'border-[var(--udla-ink)]/10 text-[var(--udla-ink-muted)]'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                <div ref={metodoPanelRef}>
                  {metodo === 'transferencia' && (
                    <div className="mt-3 rounded-xl bg-[var(--udla-bg)] px-3 py-3 text-sm text-[var(--udla-ink)]">
                      <p className="font-extrabold">Datos para transferir:</p>
                      <p>{PAGO.transferencia.banco} · {PAGO.transferencia.tipoCuenta}</p>
                      <p>Cuenta: <span className="font-extrabold tabular-nums">{PAGO.transferencia.numeroCuenta}</span></p>
                      <p>{PAGO.transferencia.titular}</p>
                      <p>CI: {PAGO.transferencia.cedula}</p>
                      <p className="mt-1 text-xs font-semibold text-[var(--udla-ink-muted)]">
                        Envía el comprobante por WhatsApp al confirmar tu pedido.
                      </p>
                    </div>
                  )}

                  {metodo === 'deuna' && (
                    <div className="mt-3 rounded-xl bg-[var(--udla-bg)] px-3 py-4 text-center text-sm text-[var(--udla-ink)]">
                      {deunaQrOk ? (
                        <>
                          <img
                            src={PAGO.deunaQrImage}
                            alt="QR de cobro De Una de Sara"
                            onError={() => setDeunaQrOk(false)}
                            className="mx-auto h-64 w-64 max-w-full rounded-lg object-contain"
                          />
                          <p className="mt-2 text-xs font-semibold text-[var(--udla-ink-muted)]">
                            Escanea con tu app De Una para pagar, luego confirma tu pedido por WhatsApp.
                          </p>
                        </>
                      ) : (
                        <p className="text-xs font-semibold text-[var(--udla-ink-muted)]">
                          El QR de De Una todavía no está disponible. Escoge otro método por ahora.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </fieldset>
            </div>
          </section>
        )}
      </main>

      {/* Barra fija inferior (visible con pedido de HOY aunque la preventa esté cerrada) */}
      {total > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-20 border-t-2 border-[var(--udla-pink-deep)]/20 bg-white/95 backdrop-blur"
             style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
          <div className="mx-auto flex max-w-[480px] items-center gap-3 px-5 py-3">
            <div className="leading-tight">
              <p className="text-xs font-bold text-[var(--udla-ink-muted)]">
                {modo === 'hoy' ? 'HOY · ' : modo === 'coordinado' ? 'A coordinar · ' : ''}{total} galleta{total === 1 ? '' : 's'}
              </p>
              <p className="text-2xl font-extrabold text-[var(--udla-ink)] tabular-nums">{money(totalUSD(items))}</p>
            </div>
            <button
              type="button"
              onClick={onSubmit}
              disabled={submitting}
              className="ml-auto flex flex-1 items-center justify-center gap-2 rounded-2xl bg-green-600 px-5 py-4 text-base font-bold text-white shadow-lg transition active:scale-95 disabled:opacity-60"
            >
              {submitting ? 'Enviando…' : COPY.ctaWhatsApp}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
